import CustomerAuthModal from '../components/CustomerAuthModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';
import CustomerProfilePage from './CustomerProfilePage';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCoreT } from '../context/CoreLangContext';
import { resolveMenuTheme, menuCssVars, buildMenuCss } from '../theme/menuTheme';
import { identify, trackPageView, trackCategoryView, trackImpressions, trackSearch, getVisitorId } from '../analytics/track';
import { restaurantApi, customerApi } from '../api/apiClient';
import { formatPrice } from '../utils/currency';

// Language → html lang + dir attributes mapping
const LANG_ATTRS = {
  en:{lang:'en',dir:'ltr'}, fa:{lang:'fa',dir:'rtl'}, ar:{lang:'ar',dir:'rtl'},
  fr:{lang:'fr',dir:'ltr'}, de:{lang:'de',dir:'ltr'}, tr:{lang:'tr',dir:'ltr'},
  es:{lang:'es',dir:'ltr'}, it:{lang:'it',dir:'ltr'}, zh:{lang:'zh',dir:'ltr'},
  ja:{lang:'ja',dir:'ltr'}, ru:{lang:'ru',dir:'ltr'}, pt:{lang:'pt',dir:'ltr'},
  ko:{lang:'ko',dir:'ltr'}, nl:{lang:'nl',dir:'ltr'}, pl:{lang:'pl',dir:'ltr'},
  he:{lang:'he',dir:'rtl'}, hi:{lang:'hi',dir:'ltr'},
};

// Build the combined "all branches" menu: the union of every branch's items,
// de-duplicated by name (case-insensitive). Each product records which branches
// carry it (`_availableAt`) and whether every branch does (`_everywhere`), so a
// card / detail page can flag items that aren't offered at all locations.
function buildAggregateMenu(activeBranches) {
  const catMap = new Map();
  const prodMap = new Map();   // key → first product seen
  const prodAt = new Map();    // key → Set(branch name)
  const keyOf = (p) => String(p?.name || '').trim().toLowerCase();
  activeBranches.forEach(b => {
    (b.categories || []).forEach(c => {
      const k = String(c?.slug || c?.name || '').toLowerCase();
      if (k && !catMap.has(k)) catMap.set(k, c);
    });
    (b.products || []).forEach(p => {
      const k = keyOf(p);
      if (!k) return;
      if (!prodMap.has(k)) prodMap.set(k, p);
      if (!prodAt.has(k)) prodAt.set(k, new Set());
      prodAt.get(k).add(b.name);
    });
  });
  const total = activeBranches.length;
  const products = [...prodMap.entries()].map(([k, p]) => {
    const at = [...(prodAt.get(k) || [])];
    return { ...p, _availableAt: at, _everywhere: at.length >= total };
  });
  return { categories: [...catMap.values()], products };
}

export default function MenuPage({ restaurant }) {
  const T = useCoreT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addItem, removeItem, getItemQuantity, getCartCount } = useCart();
  const { t, tu, translateProduct, translateCategory, restaurantLang, setRestaurantLang, restMode, restEnabled, showOriginalNames, setShowOriginalNames, isTranslating } = useLanguage();

  // ── Event popup ─────────────────────────────────────────────────────────────
  const [activeEvent, setActiveEvent] = useState(null);
  const [popupOpen,   setPopupOpen]   = useState(false);
  useEffect(() => {
    const today = new Date();
    const ev = (restaurant.events || []).find(e => {
      if (!e.active) return false;
      if (e.startDate && new Date(e.startDate) > today) return false;
      if (e.endDate   && new Date(e.endDate)   < today) return false;
      return true;
    });
    if (ev) {
      const key = `nooshin_evt_${restaurant.slug}_${ev._id}`;
      if (!sessionStorage.getItem(key)) {
        setActiveEvent(ev);
        setTimeout(() => setPopupOpen(true), 1200); // slight delay
        sessionStorage.setItem(key, '1');
      }
    }
  }, [restaurant]);

  // ── SEO: sync document meta tags + html lang/dir when language changes ────
  useEffect(() => {
    const lang = restaurantLang || restaurant.defaultLanguage || 'en';
    const attrs = LANG_ATTRS[lang] || { lang, dir: 'ltr' };

    // Update <html> lang and dir attributes
    document.documentElement.setAttribute('lang', attrs.lang);
    document.documentElement.setAttribute('dir', attrs.dir);

    // Build page title from translated content
    const trans    = restaurant.translations?.[lang] || {};
    const info     = trans.restaurantInfo || {};
    const seo      = trans.seo || {};
    const name     = info.name || restaurant.name || '';
    const tagline  = info.tagline || restaurant.tagline || '';
    const title    = seo.title || (name + (tagline ? ' — ' + tagline : '') + ' | Menu');
    document.title = title;

    // Update or create meta description
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) { desc = document.createElement('meta'); desc.setAttribute('name','description'); document.head.appendChild(desc); }
    desc.setAttribute('content', seo.description || info.description || tagline || '');

    // Update canonical link to ?lang= URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel','canonical'); document.head.appendChild(canonical); }
    const defLang = restaurant.defaultLanguage || 'en';
    const url = new URL(window.location.href);
    if (lang === defLang) { url.searchParams.delete('lang'); }
    else { url.searchParams.set('lang', lang); }
    canonical.setAttribute('href', url.toString());

    // Update URL bar to reflect language
    if (window.location.href !== url.toString()) {
      window.history.replaceState({ lang }, '', url.toString());
    }
  }, [restaurantLang, restaurant]);
  const { customer, saveCustomerProfile } = useAuth();
  const favorites = customer?.favorites || [];
  const toggleFav = async (pid) => {
    if (!customer) { setShowAuthModal(true); return; }
    try {
      const r = await customerApi.toggleFavorite(pid);
      saveCustomerProfile({ ...customer, favorites: r.data.favorites });
    } catch {}
  };

  // Share the menu link (Web Share API when available, else WhatsApp/Telegram).
  // NOTE: reads the branch from searchParams directly — branchSlugParam is
  // declared further down and a const reference here would crash (TDZ).
  const [shareOpen, setShareOpen] = useState(false);
  const shareBranch = searchParams.get('branch');
  const shareUrl  = window.location.origin + '/menu' + (shareBranch ? `?branch=${shareBranch}` : '');
  const shareText = `${restaurant.name} — ${tu('nav_menu')}`;
  const doShare = () => {
    if (navigator.share) { navigator.share({ title: shareText, url: shareUrl }).catch(() => {}); }
    else setShareOpen(o => !o);
  };

  // ── Branch resolution ─────────────────────────────────────────────────────
  // ?branch=slug in the URL means we're viewing a specific branch.
  // The branch has its own products/categories. If no ?branch, show main restaurant.
  const branchSlugParam = searchParams.get('branch') || null;
  const activeBranches = (restaurant.branches || []).filter(b => b.isActive);
  const activeBranch = branchSlugParam
    ? activeBranches.find(b => b.slug === branchSlugParam)
    : null;
  // With branches, the bare domain shows a COMBINED "all branches" menu (union
  // of every branch, de-duped by name) — unless the owner turned it off.
  const aggregateEnabled = restaurant.aggregateMenu !== false && activeBranches.length > 0;
  const showAggregated = !activeBranch && aggregateEnabled;
  const aggregateSource = useMemo(
    () => (showAggregated ? buildAggregateMenu(activeBranches) : null),
    [showAggregated, restaurant]
  );
  // The "source" is whatever has the products/categories we show
  const menuSource = activeBranch || aggregateSource || restaurant;
  const branchLabel = activeBranch ? activeBranch.name : null;
  // Preserve the branch context through navigation so the item/cart pages look
  // products up in the same source (branch products have their own ids).
  const branchQ = branchSlugParam ? `?branch=${branchSlugParam}` : '';

  // Server returns _id on categories and products; use slug for category matching
  const firstCat = menuSource.categories[0]?.slug || null;
  const [activeCatSlug,  setActiveCatSlug]  = useState(firstCat);
  const [activeSubCat,   setActiveSubCat]   = useState(null);
  const [showAuthModal,  setShowAuthModal]  = useState(false);
  const [showProfile,    setShowProfile]    = useState(false);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [searchOpen,     setSearchOpen]     = useState(false); // mobile: search collapses to an icon
  const catBarRef = useRef(null);

  const primaryColor = restaurant.primaryColor;
  const cartCount    = getCartCount(restaurant.slug);

  // ── Menu design (theme + layout + custom palette) ─────────────────────────
  const { theme: menuTheme, layout: menuLayout, colors: menuColors } = resolveMenuTheme(restaurant);

  const translatedProducts   = menuSource.products.map(translateProduct);
  const translatedCategories = menuSource.categories.map(translateCategory);
  const activeCat = translatedCategories.find(c => c.slug === activeCatSlug);
  // While searching, search the WHOLE menu (name + description), not just the
  // active category — otherwise matches in other categories are invisible.
  const filteredProducts = translatedProducts.filter(p => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return p.name.toLowerCase().includes(q) ||
             (p.description || '').toLowerCase().includes(q);
    }
    const matchCat = activeCatSlug ? p.categorySlug === activeCatSlug : true;
    const matchSub = activeSubCat  ? p.subCategory  === activeSubCat  : true;
    return matchCat && matchSub;
  });

  // Typo-tolerant fallback: the in-browser filter above is instant but exact.
  // When it finds nothing for a real query (e.g. a misspelling), ask the server
  // search (Meilisearch, with a Mongo fallback) and map the hits back to the
  // products we already have so the cards render identically/themed. If search
  // is unavailable the endpoint just returns Mongo matches (or nothing).
  const [fallbackResults, setFallbackResults] = useState(null);
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2 || filteredProducts.length > 0) { setFallbackResults(null); return; }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const res = await restaurantApi.searchMenu(restaurant.slug, q);
        const hits = (res && res.data) || [];
        const byId = new Map(translatedProducts.map(p => [String(p._id || p.id), p]));
        const mapped = hits.map(h => byId.get(String(h.productId))).filter(Boolean);
        if (!cancelled) setFallbackResults(mapped);
      } catch { if (!cancelled) setFallbackResults([]); }
    }, 350);
    return () => { cancelled = true; clearTimeout(id); };
    // eslint-disable-next-line
  }, [searchQuery, filteredProducts.length, restaurant.slug]);

  // What the grid renders: exact in-browser matches, else the typo-tolerant
  // fallback results (only relevant while searching).
  const displayProducts = (searchQuery.trim() && filteredProducts.length === 0 && fallbackResults)
    ? fallbackResults
    : filteredProducts;

  const handleCategoryClick = (slug) => {
    setActiveCatSlug(slug);
    setActiveSubCat(null);
  };

  // ── Behavioural tracking ──────────────────────────────────────────────────
  useEffect(() => { identify(customer?._id || null); }, [customer]);
  // Page view once per mount
  useEffect(() => { trackPageView({ categorySlug: activeCatSlug, lang: restaurantLang }); /* eslint-disable-next-line */ }, []);
  // Category view + product impressions when the active category changes
  useEffect(() => {
    if (!activeCatSlug) return;
    trackCategoryView(activeCatSlug);
    const shown = translatedProducts.filter(p => p.categorySlug === activeCatSlug).slice(0, 30);
    trackImpressions(shown, activeCatSlug);
    // eslint-disable-next-line
  }, [activeCatSlug]);
  // Debounced search tracking
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) return;
    const id = setTimeout(() => trackSearch(searchQuery), 800);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // ── Personalised "Recommended for you" strip ──────────────────────────────
  const [recs, setRecs] = useState([]);
  useEffect(() => {
    let alive = true;
    restaurantApi.getRecommendations(restaurant.slug, { visitorId: getVisitorId(), limit: 8 })
      .then(r => { if (alive) setRecs((r.data || []).map(translateProduct)); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [restaurant.slug, branchSlugParam]);

  // Helpers for popup
  const evtFeatured = activeEvent
    ? translatedProducts.filter(p => (activeEvent.featuredProducts||[]).includes(p._id||p.id))
    : [];

  const POPUP_STYLES = {
    elegant:  { bg: 'linear-gradient(145deg,#1a1a2e 0%,#16213e 100%)', accent: '#c8a96e', glow: 'rgba(200,169,110,0.3)' },
    luxury:   { bg: 'linear-gradient(145deg,#1a1505 0%,#2d2210 100%)', accent: '#f5c842', glow: 'rgba(245,200,66,0.35)'  },
    playful:  { bg: 'linear-gradient(145deg,#1a0530 0%,#2d0b5a 100%)', accent: '#c084fc', glow: 'rgba(192,132,252,0.35)' },
    warm:     { bg: 'linear-gradient(145deg,#1a0d05 0%,#2d1a08 100%)', accent: '#fb923c', glow: 'rgba(251,146,60,0.3)'   },
    dark:     { bg: 'linear-gradient(145deg,#050505 0%,#111 100%)',     accent: '#e2e8f0', glow: 'rgba(226,232,240,0.2)' },
    vibrant:  { bg: 'linear-gradient(145deg,#050a2d 0%,#0a1a4d 100%)', accent: '#38bdf8', glow: 'rgba(56,189,248,0.3)'  },
  };

  return (
    <div className={`menu-page theme-${menuTheme} layout-${menuLayout}`} style={menuCssVars(menuColors)}>

      {/* ── Event Popup ─────────────────────────────────────────────────────── */}
      {popupOpen && activeEvent && (() => {
        const style = POPUP_STYLES[activeEvent.popupStyle] || POPUP_STYLES.elegant;
        const sym   = restaurant.currencyCode
          ? (new Intl.NumberFormat('en',{style:'currency',currency:restaurant.currencyCode}).formatToParts(0).find(p=>p.type==='currency')?.value || '$')
          : '$';
        return (
          <div className="evtpop-backdrop" onClick={() => setPopupOpen(false)}>
            <div className="evtpop-sheet" style={{ background: style.bg, '--accent': style.accent, '--glow': style.glow }}
              onClick={e => e.stopPropagation()}>

              {/* Close */}
              <button className="evtpop-close" onClick={() => setPopupOpen(false)}>✕</button>

              {/* Banner image */}
              {activeEvent.bannerImage && (
                <div className="evtpop-banner-wrap">
                  <img src={activeEvent.bannerImage} alt="" className="evtpop-banner" />
                  <div className="evtpop-banner-overlay" />
                </div>
              )}

              {/* Body */}
              <div className="evtpop-body">
                <div className="evtpop-glow" style={{ background: style.glow }} />

                <div className="evtpop-title">{activeEvent.title || 'Special Event'}</div>
                {activeEvent.message && (
                  <p className="evtpop-message">{activeEvent.message}</p>
                )}

                {/* Discount badge */}
                {activeEvent.discountType && activeEvent.discountType !== 'none' && (
                  <div className="evtpop-discount" style={{ borderColor: style.accent, color: style.accent }}>
                    <span className="evtpop-discount-amount">
                      {activeEvent.discountType === 'percentage'
                        ? `${activeEvent.discountValue}% OFF`
                        : `${sym}${activeEvent.discountValue} OFF`}
                    </span>
                    {activeEvent.discountCode && (
                      <span className="evtpop-code" style={{ background: `${style.accent}22` }}>
                        Code: <strong>{activeEvent.discountCode}</strong>
                      </span>
                    )}
                  </div>
                )}

                {/* Featured products */}
                {evtFeatured.length > 0 && (
                  <div className="evtpop-products">
                    <div className="evtpop-products-label">✦ Featured For This Event</div>
                    <div className="evtpop-products-grid">
                      {evtFeatured.slice(0,4).map(p => {
                        const pid = p._id || p.id;
                        return (
                          <div key={pid} className="evtpop-product" onClick={() => { setPopupOpen(false); navigate(`/item/${pid}${branchQ}`); }}>
                            {p.image && <img src={p.image} alt={p.name} className="evtpop-product-img" onError={e=>e.target.style.display='none'} />}
                            <div className="evtpop-product-info">
                              <span className="evtpop-product-name">{p.name}</span>
                              <span className="evtpop-product-price" style={{ color: style.accent }}>
                                {new Intl.NumberFormat('en',{style:'currency',currency:restaurant.currencyCode||'USD',minimumFractionDigits:0}).format(p.price)}
                              </span>
                            </div>
                            <span className="evtpop-product-arrow" style={{ color: style.accent }}>→</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <button className="evtpop-cta" style={{ background: style.accent, boxShadow: `0 8px 24px ${style.glow}` }}
                  onClick={() => setPopupOpen(false)}>
                  Explore Our Menu →
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Branch banner — shown when viewing a specific branch */}
      {activeBranch && (
        <div className="branch-banner">
          <span className="branch-banner-icon">📍</span>
          <div className="branch-banner-text">
            <strong>{activeBranch.name}</strong>
            {activeBranch.address && <span>{activeBranch.address}</span>}
          </div>
          <div className="branch-switch-tabs">
            {aggregateEnabled && <a href="/menu" className="branch-switch-tab all">🏢 {tu('menu_all_branches')}</a>}
            {activeBranches.filter(b => b.slug !== activeBranch.slug).map(b => (
              <a key={b.slug} className="branch-switch-tab"
                 href={`/menu?branch=${encodeURIComponent(b.slug)}`}>{b.name}</a>
            ))}
          </div>
        </div>
      )}
      {/* Combined "all branches" banner — with a picker to jump to one branch */}
      {showAggregated && (
        <div className="branch-banner">
          <span className="branch-banner-icon">🏢</span>
          <div className="branch-banner-text">
            <strong>{tu('menu_all_branches')}</strong>
            <span>{tu('menu_pick_branch')}</span>
          </div>
          <div className="branch-switch-tabs">
            {activeBranches.map(b => (
              <a key={b.slug} className="branch-switch-tab"
                 href={`/menu?branch=${encodeURIComponent(b.slug)}`}>{b.name}</a>
            ))}
          </div>
        </div>
      )}
      {/* Top Nav */}
      <nav className={`menu-nav ${searchOpen ? 'search-open' : ''}`}>
        <button className="back-btn" onClick={() => { window.location.href = window.location.origin + '/'; }}>← {restaurant.name}{branchLabel ? ` · ${branchLabel}` : ''}</button>
        <div className="nav-search">
          <span className="search-icon">🔍</span>
          <input type="text" {...{placeholder: tu('menu_search')}} value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)} className="search-input" />
        </div>
        <div className="nav-actions">
          <button className="icon-btn nav-search-btn" onClick={() => setSearchOpen(o => !o)} aria-label={tu('menu_search')}>
            {searchOpen ? '✕' : '🔍'}
          </button>
          <div className="share-wrap">
            <button className="icon-btn" onClick={doShare} aria-label={tu('menu_share')}>📤</button>
            {shareOpen && (
              <div className="share-pop" onClick={() => setShareOpen(false)}>
                <a className="share-opt" href={`https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`} target="_blank" rel="noopener noreferrer">💬 {tu('share_whatsapp')}</a>
                <a className="share-opt" href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer">✈️ {tu('share_telegram')}</a>
                <button className="share-opt" onClick={() => { navigator.clipboard?.writeText(shareUrl); }}>🔗 {tu('share_copy')}</button>
              </div>
            )}
          </div>
          <button className="cart-btn icon-btn" onClick={() => navigate(`/cart${branchQ}`)}>
            🛒
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
          {restMode === 'multi' && restEnabled.length > 1 && (
            <LanguageSwitcher
              enabled={restEnabled}
              current={restaurantLang}
              onChange={setRestaurantLang}
              compact={false}
            />
          )}
          <button className="profile-btn icon-btn" onClick={() => customer ? setShowProfile(true) : setShowAuthModal(true)}>
            {customer ? <span className="avatar">{customer.name?.[0]?.toUpperCase()}</span> : '👤'}
          </button>
        </div>
      </nav>

      {/* Body — category bar + content. The .menu-body wrapper lets the
          category bar switch between a top bar (horizontal) and a left
          column (sidebar) purely via CSS. */}
      <div className="menu-body">
        {/* Category Bar */}
        <div className="category-bar" ref={catBarRef}>
          {translatedCategories.map(cat => (
            <button key={cat._id || cat.slug}
              className={`cat-pill ${activeCatSlug === cat.slug ? 'active' : ''}`}
              onClick={() => handleCategoryClick(cat.slug)}>
              <span className="cat-icon">{cat.icon}</span>
              <span className="cat-name">{cat.name}</span>
            </button>
          ))}
        </div>

        <div className="menu-content">
          {/* Recommended for you */}
          {!searchQuery && recs.length > 0 && (
            <div className="rec-strip">
              <div className="rec-strip-head">
                <span className="rec-strip-title">{tu('menu_related') || 'Recommended for you'}</span>
                <span className="rec-strip-badge">✦ For You</span>
              </div>
              <div className="rec-row">
                {recs.map(p => (
                  <div key={p._id} className="rec-card" onClick={() => navigate(`/item/${p._id}${branchQ}`)}>
                    {p.image
                      ? <img src={p.image} alt={p.name} className="rec-card-img" onError={e => { e.target.style.display = 'none'; }} />
                      : <div className="rec-card-fallback">🍽️</div>}
                    <div className="rec-card-body">
                      <div className="rec-card-name">{p.name}</div>
                      <div className="rec-card-price">{formatPrice(p.price, restaurant.currencyCode)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sub-categories */}
          {activeCat?.subCategories?.length > 0 && (
            <div className="subcategory-bar">
              <button className={`sub-pill ${!activeSubCat ? 'active' : ''}`}
                onClick={() => setActiveSubCat(null)}>{tu('menu_all')}</button>
              {activeCat.subCategories.map(sub => (
                <button key={sub} className={`sub-pill ${activeSubCat === sub ? 'active' : ''}`}
                  onClick={() => setActiveSubCat(sub)}>{sub}</button>
              ))}
            </div>
          )}

          {/* Section header */}
          <div className="section-header">
            <h2 className="section-title">
              {searchQuery ? `${tu('menu_results_for')} "${searchQuery}"` : activeCat?.name || tu('nav_menu')}
            </h2>
            {activeCat?.description && !searchQuery && (
              <p className="section-sub">{activeCat.description}</p>
            )}
            <span className="item-count">{displayProducts.length} {tu('menu_items')}</span>
            {isTranslating && (
              <button
                type="button"
                className={`orig-names-toggle ${showOriginalNames ? 'on' : ''}`}
                onClick={() => setShowOriginalNames(v => !v)}
                aria-pressed={showOriginalNames}
              >
                🔤 {tu('menu_show_original')}
              </button>
            )}
          </div>

          {/* Products */}
          <div className="products-grid">
            {displayProducts.length === 0 ? (
              <div className="empty-state"><div style={{ fontSize: '3rem' }}>🍽️</div><p>{tu('menu_no_items')}</p></div>
            ) : displayProducts.map(product => (
              <ProductCard
                isFav={favorites.includes(String(product._id))}
                onToggleFav={() => toggleFav(String(product._id))}
                key={product._id}
                product={product}
                restaurantSlug={restaurant.slug}
                currencyCode={restaurant.currencyCode}
                primaryColor={primaryColor}
                theme={menuTheme}
                onNavigate={() => navigate(`/item/${product._id}${branchQ}`)}
              />
            ))}
          </div>

          {/* Powered-by badge — every menu quietly advertises the platform */}
          <a className="powered-by" href={`https://${window.location.hostname.split('.').slice(-2).join('.')}/`} target="_blank" rel="noopener noreferrer">
            {tu('menu_powered_by')} <strong>Nowshin</strong> ✦
          </a>
        </div>
      </div>

      {/* Floating cart */}
      {cartCount > 0 && (
        <div className="floating-cart" onClick={() => navigate('/cart')}>
          <span>{cartCount} {tu('menu_in_cart')}</span>
          <span>{tu('menu_view_cart')}</span>
        </div>
      )}

      {showAuthModal && <CustomerAuthModal onClose={() => setShowAuthModal(false)} />}
      {showProfile && <CustomerProfilePage restaurant={restaurant} onClose={() => setShowProfile(false)} />}

      <style>{buildMenuCss()}</style>
      <style>{`
        /* ── Event Popup (modal overlay — intentionally dark) ─────────────── */
        .evtpop-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(16px); z-index: 9000; display: flex; align-items: flex-end; justify-content: center; padding: 1rem; animation: evtFadeIn .3s ease; }
        @media(min-width:600px) { .evtpop-backdrop { align-items: center; } }
        @keyframes evtFadeIn { from{opacity:0} to{opacity:1} }
        .evtpop-sheet { position: relative; width: 100%; max-width: 480px; border-radius: 28px 28px 20px 20px; overflow: hidden; border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05); animation: evtSlideUp .4s cubic-bezier(0.34,1.56,0.64,1); }
        @media(min-width:600px) { .evtpop-sheet { border-radius: 28px; } }
        @keyframes evtSlideUp { from{transform:translateY(60px);opacity:0} to{transform:translateY(0);opacity:1} }
        .evtpop-close { position: absolute; top: 1rem; right: 1rem; z-index: 10; background: rgba(0,0,0,0.45); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.15); color: rgba(255,255,255,0.7); width: 32px; height: 32px; border-radius: 50%; cursor: pointer; font-size: 0.75rem; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .evtpop-close:hover { background: rgba(255,255,255,0.15); color: #fff; }
        .evtpop-banner-wrap { position: relative; height: 200px; overflow: hidden; }
        .evtpop-banner { width: 100%; height: 100%; object-fit: cover; display: block; }
        .evtpop-banner-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.8) 100%); }
        .evtpop-body { position: relative; padding: 1.5rem 1.5rem 1.75rem; display: flex; flex-direction: column; align-items: center; gap: 0.85rem; text-align: center; }
        .evtpop-glow { position: absolute; top: -60px; left: 50%; transform: translateX(-50%); width: 220px; height: 220px; border-radius: 50%; filter: blur(60px); opacity: 0.35; pointer-events: none; }
        .evtpop-title { font-family: 'Playfair Display', serif; font-size: 1.6rem; font-weight: 900; color: #fff; line-height: 1.15; position: relative; z-index: 1; }
        .evtpop-message { font-size: 0.88rem; color: rgba(255,255,255,0.6); line-height: 1.65; max-width: 340px; margin: 0; position: relative; z-index: 1; }
        .evtpop-discount { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; padding: 0.65rem 1.25rem; border: 1px solid; border-radius: 14px; position: relative; z-index: 1; }
        .evtpop-discount-amount { font-size: 1.5rem; font-weight: 900; font-family: 'Playfair Display', serif; }
        .evtpop-code { font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.6rem; border-radius: 8px; letter-spacing: 0.08em; }
        .evtpop-products { width: 100%; position: relative; z-index: 1; }
        .evtpop-products-label { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(255,255,255,0.35); margin-bottom: 0.5rem; }
        .evtpop-products-grid { display: flex; flex-direction: column; gap: 0.35rem; }
        .evtpop-product { display: flex; align-items: center; gap: 0.65rem; padding: 0.55rem 0.75rem; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; cursor: pointer; transition: all 0.2s; text-align: left; }
        .evtpop-product:hover { background: rgba(255,255,255,0.1); transform: translateX(3px); }
        .evtpop-product-img { width: 40px; height: 40px; border-radius: 8px; object-fit: cover; flex-shrink: 0; }
        .evtpop-product-info { flex: 1; display: flex; flex-direction: column; gap: 0.1rem; }
        .evtpop-product-name { font-size: 0.8rem; font-weight: 700; color: #fff; }
        .evtpop-product-price { font-size: 0.72rem; font-weight: 700; }
        .evtpop-product-arrow { font-size: 0.9rem; flex-shrink: 0; opacity: 0.7; }
        .evtpop-cta { padding: 0.85rem 2rem; border: none; border-radius: 14px; color: #fff; font-family: 'DM Sans', sans-serif; font-size: 0.92rem; font-weight: 800; cursor: pointer; transition: all 0.2s; width: 100%; position: relative; z-index: 1; letter-spacing: 0.02em; }
        .evtpop-cta:hover { opacity: 0.88; transform: scale(1.02); }
        /* Branch banner */
        .branch-banner { display: flex; align-items: center; gap: 1rem; background: rgba(200,169,110,.08); border-bottom: 1px solid rgba(200,169,110,.2); padding: .75rem 1.5rem; }
        .branch-banner-icon { font-size: 1.25rem; }
        .branch-banner-text { flex: 1; display: flex; flex-direction: column; gap: .1rem; }
        .branch-banner-text strong { color: #fff; font-size: .9rem; }
        .branch-banner-text span { font-size: .75rem; color: rgba(255,255,255,.45); }
        .branch-banner-change { font-size: .78rem; color: var(--primary); text-decoration: none; white-space: nowrap; border: 1px solid rgba(200,169,110,.3); border-radius: 8px; padding: .3rem .75rem; transition: background .2s; }
        .branch-banner-change:hover { background: rgba(200,169,110,.1); }
        .branch-switch-tabs { display: flex; gap: .4rem; flex-wrap: wrap; justify-content: flex-end; }
        .branch-switch-tab { font-size: .78rem; font-weight: 700; color: var(--primary); text-decoration: none; white-space: nowrap; border: 1px solid rgba(200,169,110,.3); border-radius: 8px; padding: .3rem .75rem; transition: background .2s; }
        .branch-switch-tab:hover { background: rgba(200,169,110,.12); }
        .branch-switch-tab.all { background: rgba(200,169,110,.14); }
      `}</style>
    </div>
  );
}

function ProductCard({ product, restaurantSlug, currencyCode, primaryColor, theme, onNavigate, isFav, onToggleFav }) {
  const { addItem, removeItem, getItemQuantity } = useCart();
  const { tu, showOriginalNames } = useLanguage();
  const qty = getItemQuantity(restaurantSlug, product._id);
  const origName = showOriginalNames && product._orig?.name && product._orig.name !== product.name
    ? product._orig.name : '';
  const [imgErr, setImgErr] = useState(false);

  const handleAdd    = (e) => { e.stopPropagation(); addItem(restaurantSlug, { ...product, id: product._id }); };
  const handleRemove = (e) => { e.stopPropagation(); removeItem(restaurantSlug, product._id); };

  return (
    <div className={`product-card pc-${theme || 'classic'} ${!product.available ? 'unavailable' : ''}`}
      onClick={onNavigate}>
      <div className="card-img-wrap">
        {!imgErr
          ? <img src={product.image} alt={product.name} className="card-img" onError={() => setImgErr(true)} />
          : <div className="card-img-fallback">🍽️</div>
        }
        {!product.available && <div className="unavailable-overlay">{tu('menu_unavailable')}</div>}
        <button className={`fav-btn ${isFav ? 'on' : ''}`}
          aria-label={isFav ? tu('fav_remove') : tu('fav_add')}
          onClick={e => { e.stopPropagation(); onToggleFav && onToggleFav(); }}>
          {isFav ? '♥' : '♡'}
        </button>
        <div className="card-tags">
          {product.tags?.slice(0, 2).map(tag => (
            <span key={tag} className={`tag tag-${tag.toLowerCase().replace(/[^a-z]/g, '-')}`}>{tag}</span>
          ))}
        </div>
      </div>
      <div className="card-body">
        <h3 className="card-name">{product.name}</h3>
        {origName && <div className="card-name-orig" dir="auto">{origName}</div>}
        {product._availableAt && !product._everywhere && product._availableAt.length > 0 && (
          <div className="card-branch-tag" title={product._availableAt.join(', ')}>
            📍 {tu('menu_only_at')}: {product._availableAt.join('، ')}
          </div>
        )}
        <p className="card-desc">{product.description}</p>
        {product.options?.length > 0 && (
          <div className="card-options">
            {product.options.map((o, i) => (
              <span key={i} className="card-option" title={o.label}>
                {o.icon ? <span className="card-option-icon">{o.icon}</span> : null}
                <span className="card-option-label">{o.label}</span>
              </span>
            ))}
          </div>
        )}
        <div className="card-footer">
          <span className="card-price">{formatPrice(product.price, currencyCode)}</span>
          {product.available && (
            <div className="qty-control" onClick={e => e.stopPropagation()}>
              {qty > 0 ? (
                <>
                  <button className="qty-btn" onClick={handleRemove}>−</button>
                  <span className="qty-num">{qty}</span>
                  <button className="qty-btn qty-btn-add" onClick={handleAdd}>+</button>
                </>
              ) : (
                <button className="add-btn" onClick={handleAdd}>{tu('menu_add')}</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
